import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/super-admin';

const SuperAdminForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, {
        email: email.toLowerCase().trim()
      });
      
      setSubmitted(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700" data-testid="super-admin-forgot-password-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-xl overflow-hidden mb-4">
            <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
          </div>
          <CardTitle className="text-2xl text-white">
            {submitted ? 'Check Your Email' : 'Forgot Password'}
          </CardTitle>
          <CardDescription className="text-slate-400">
            {submitted 
              ? 'If an admin account exists with that email, we sent you a reset link.'
              : 'Enter your email to receive a password reset link'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {submitted ? (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-slate-300">
                  We sent a password reset link to <strong className="text-white">{email}</strong>
                </p>
                <p className="text-sm text-slate-400">
                  The link will expire in <strong className="text-[#ffd108]">15 minutes</strong>.
                </p>
              </div>
              
              <div className="space-y-3 pt-4">
                <p className="text-xs text-slate-500">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <Button
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                  onClick={() => setSubmitted(false)}
                  data-testid="try-again-button"
                >
                  Try Again
                </Button>
              </div>
              
              <div className="pt-4 border-t border-slate-700">
                <Link
                  to="/super-admin/login"
                  className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@equipr.com"
                  className="bg-slate-700/50 border-slate-600 text-white"
                  required
                />
                <p className="text-xs text-slate-500">
                  Enter the email address associated with your admin account
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#ffd108] hover:bg-[#e5bc07] text-black font-semibold" 
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Reset Link
                  </>
                )}
              </Button>

              <div className="pt-4 border-t border-slate-700 text-center">
                <Link
                  to="/super-admin/login"
                  className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminForgotPassword;
